import type {
  EpisodeRef,
  OutlineNode,
  OutputRef,
  SceneView,
  ScriptDoc,
  ScriptRow,
  ScriptScene,
  ShotView,
  StoryboardView,
  WorldNode,
  WorldRule,
  WorldView,
} from "@dramapie/shared";

/**
 * 世界树 / 大纲 / 剧本的**视图纯函数**。
 *
 * M7 之后数据来自后端真模型（`WorldView` / `OutlineView` / `ScriptView`），
 * 这里不做任何“适配/编造”：只负责建树、排序、过滤、把 `WorldNode.sceneIds`
 * join 到 storyboard 的场次上、把分镜稿行按场次分组。
 *
 * 没有 React、没有网络，组件逐条消费。
 */

// ---------------------------------------------------------------- 世界树

/** 按 `parentId` 建索引，每组内按 `order` 排序（同 order 用 id 兜底，保证稳定）。 */
export function worldChildren(nodes: readonly WorldNode[]): Map<string | null, WorldNode[]> {
  const map = new Map<string | null, WorldNode[]>();
  for (const node of nodes) {
    const key = node.parentId ?? null;
    const list = map.get(key) ?? [];
    list.push(node);
    map.set(key, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  }
  return map;
}

/** 根 = `parentId` 为空，或 parent 指向不存在的节点（坏数据也不藏起来）。 */
export function worldRoots(nodes: readonly WorldNode[]): WorldNode[] {
  const ids = new Set(nodes.map((node) => node.id));
  const children = worldChildren(nodes);
  const candidates = [
    ...(children.get(null) ?? []),
    ...nodes.filter((node) => node.parentId !== null && !ids.has(node.parentId)),
  ];
  return candidates
    .filter((node, index, list) => list.findIndex((item) => item.id === node.id) === index)
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

export function worldNodeById(nodes: readonly WorldNode[], id: string | null): WorldNode | null {
  if (!id) return null;
  return nodes.find((node) => node.id === id) ?? null;
}

/** 根 → 节点 的路径（面包屑、展开祖先、判断层级）。 */
export function worldPath(nodes: readonly WorldNode[], id: string): WorldNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node] as const));
  const path: WorldNode[] = [];
  const seen = new Set<string>();
  let node = byId.get(id) ?? null;
  while (node && !seen.has(node.id)) {
    seen.add(node.id);
    path.unshift(node);
    node = node.parentId ? (byId.get(node.parentId) ?? null) : null;
  }
  return path;
}

/** 节点深度：根 = 1。 */
export function worldDepth(nodes: readonly WorldNode[], id: string): number {
  return worldPath(nodes, id).length;
}

/** 节点下所有后代（不含自己），用于子节点分区与场景汇总。 */
export function worldDescendants(nodes: readonly WorldNode[], id: string): WorldNode[] {
  const children = worldChildren(nodes);
  const out: WorldNode[] = [];
  const stack = [...(children.get(id) ?? [])];
  const seen = new Set<string>([id]);
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || seen.has(node.id)) continue;
    seen.add(node.id);
    out.push(node);
    stack.push(...(children.get(node.id) ?? []));
  }
  return out;
}

export interface WorldFilterState {
  query: string;
  /** 产出线：`"all"` = 全部；否则匹配 `node.outputs` 里的字符串。 */
  outputId: string;
  /** 集号：null = 全部集数。 */
  episodeNo: number | null;
}

export const EMPTY_WORLD_FILTER: WorldFilterState = {
  query: "",
  outputId: "all",
  episodeNo: null,
};

/** `WorldNode.sceneIds` 与 storyboard 场次的 join 结果（不复制真源）。 */
export interface WorldSceneAsset {
  sceneId: string;
  nodeId: string;
  name: string;
  time: string;
  episodeNo: number;
  shotCount: number;
  duration: number;
  coverUrl: string | null;
  scene: SceneView;
}

export interface SceneIndex {
  bySceneId: ReadonlyMap<string, WorldSceneAsset>;
}

function signedUrl(value: string | null | undefined): string | null {
  return typeof value === "string" && /^https?:\/\//i.test(value) ? value : null;
}

function sceneCoverUrl(sceneId: string, shots: readonly ShotView[]): string | null {
  for (const shot of shots) {
    if (shot.sceneId !== sceneId) continue;
    const url = signedUrl(shot.keyframe?.url) ?? signedUrl(shot.endFrame?.url);
    if (url) return url;
  }
  return null;
}

/**
 * 场次索引：把 storyboard 的场次按 id 铺平，带集号与首帧。
 * 只用于「世界节点 → 可拍场景」的关联，不生成任何场次。
 */
export function sceneIndex(
  storyboard: StoryboardView | null | undefined,
  episodeNo?: number | null,
): SceneIndex {
  const bySceneId = new Map<string, WorldSceneAsset>();
  if (!storyboard) return { bySceneId };
  const no = episodeNo ?? storyboard.episode.no;
  for (const scene of storyboard.scenes) {
    const shots = storyboard.shots.filter((shot) => shot.sceneId === scene.id);
    bySceneId.set(scene.id, {
      sceneId: scene.id,
      nodeId: "",
      name: scene.name,
      time: scene.time,
      episodeNo: no,
      shotCount: shots.length,
      duration: shots.reduce((sum, shot) => sum + shot.duration, 0),
      coverUrl: sceneCoverUrl(scene.id, storyboard.shots),
      scene,
    });
  }
  return { bySceneId };
}

/**
 * 单节点是否命中过滤。
 *
 * - 文字：名字 / 摘要包含（大小写不敏感）；
 * - 产出线：`outputs` 为空视为项目级（全部命中），否则必须包含；
 * - 集数：`sceneIds` 解析到的场次集号命中，或 `firstChapter` 等于该集。
 */
export function worldNodeMatches(
  node: WorldNode,
  filter: WorldFilterState,
  sceneLookup: SceneIndex,
): boolean {
  const query = filter.query.trim().toLowerCase();
  if (query) {
    const haystack = `${node.name} ${node.summary ?? ""}`.toLowerCase();
    if (!haystack.includes(query)) return false;
  }

  if (filter.outputId !== "all" && node.outputs.length > 0) {
    if (!node.outputs.includes(filter.outputId)) return false;
  }

  if (filter.episodeNo !== null) {
    const sceneMatch = node.sceneIds.some(
      (sceneId) => sceneLookup.bySceneId.get(sceneId)?.episodeNo === filter.episodeNo,
    );
    if (!sceneMatch && node.firstChapter !== filter.episodeNo) return false;
  }

  return true;
}

export interface WorldVisibility {
  matched: ReadonlySet<string>;
  /** 命中节点 + 所有上级（上级保留作上下文）。 */
  visible: ReadonlySet<string>;
}

/**
 * 过滤的可见集合：**不改树结构**，只给 UI 标记显示/淡化。
 * 命中节点的上级一律保留（原型要求“命中节点上级保留”）。
 */
export function worldVisibility(
  nodes: readonly WorldNode[],
  filter: WorldFilterState,
  sceneLookup: SceneIndex,
): WorldVisibility {
  const matched = new Set<string>();
  for (const node of nodes) {
    if (worldNodeMatches(node, filter, sceneLookup)) matched.add(node.id);
  }
  const visible = new Set(matched);
  for (const id of matched) {
    for (const ancestor of worldPath(nodes, id)) visible.add(ancestor.id);
  }
  return { matched, visible };
}

/** 过滤命中时自动选中：第一个命中且直接挂场景的**最深**节点。 */
export function firstMatchedSceneNode(
  nodes: readonly WorldNode[],
  filter: WorldFilterState,
  sceneLookup: SceneIndex,
): WorldNode | null {
  const candidates = nodes.filter(
    (node) => node.sceneIds.length > 0 && worldNodeMatches(node, filter, sceneLookup),
  );
  candidates.sort((a, b) => worldDepth(nodes, b.id) - worldDepth(nodes, a.id));
  return candidates[0] ?? null;
}

/**
 * 某节点下（含后代）关联到的可拍场景。
 * 场景数据来自 storyboard，不复制；节点只是引用 `sceneIds`。
 */
export function scenesForNode(
  nodes: readonly WorldNode[],
  sceneLookup: SceneIndex,
  nodeId: string,
): WorldSceneAsset[] {
  const ids = new Set<string>([nodeId]);
  for (const node of worldDescendants(nodes, nodeId)) ids.add(node.id);
  const seen = new Set<string>();
  const scenes: WorldSceneAsset[] = [];
  for (const id of ids) {
    const node = worldNodeById(nodes, id);
    if (!node) continue;
    for (const sceneId of node.sceneIds) {
      if (seen.has(sceneId)) continue;
      seen.add(sceneId);
      const asset = sceneLookup.bySceneId.get(sceneId);
      if (asset) scenes.push({ ...asset, nodeId: node.id });
    }
  }
  return scenes.sort(
    (a, b) => a.episodeNo - b.episodeNo || a.name.localeCompare(b.name, "zh"),
  );
}

/** 节点自己的图片（只取有签名 URL 的；不拿 key 拼地址）。 */
export function worldNodeImages(node: WorldNode): { src: string; kind: string }[] {
  const images: { src: string; kind: string }[] = [];
  for (const image of node.images) {
    const src = signedUrl(image.url);
    if (src) images.push({ src, kind: image.kind });
  }
  return images;
}

/** 产出线下拉：从 `nodes[].outputs` 去重，标题优先用项目 outputs 的真名。 */
export function worldOutputOptions(
  nodes: readonly WorldNode[],
  outputs: readonly OutputRef[],
): { id: string; title: string }[] {
  const seen = new Set<string>();
  const options: { id: string; title: string }[] = [];
  for (const node of nodes) {
    for (const value of node.outputs) {
      if (!value || seen.has(value)) continue;
      seen.add(value);
      options.push({ id: value, title: outputs.find((output) => output.id === value)?.title ?? value });
    }
  }
  return options;
}

/** 规则卡的适用范围文案：`全项目` 或产出线标题。 */
export function worldRuleScopeNames(rule: WorldRule, outputs: readonly OutputRef[]): string[] {
  if (rule.scope.all) return ["全项目"];
  return rule.scope.outputIds.map(
    (id) => outputs.find((output) => output.id === id)?.title ?? id,
  );
}

/** 世界视图的顶部统计：`N 处地点 · M 条设定`。 */
export function worldCounts(world: WorldView | null | undefined): { nodes: number; rules: number } {
  return { nodes: world?.nodes.length ?? 0, rules: world?.rules.length ?? 0 };
}

// ---------------------------------------------------------------- 大纲

export const OUTLINE_LEVEL_LABEL: Record<number, string> = {
  1: "卷级",
  2: "单元级",
  3: "集级",
};

export function flattenOutline(node: OutlineNode | null | undefined): OutlineNode[] {
  if (!node) return [];
  return [node, ...node.children.flatMap(flattenOutline)];
}

export function findOutlineNode(
  root: OutlineNode | null | undefined,
  id: string | null,
): OutlineNode | null {
  if (!root || !id) return null;
  return flattenOutline(root).find((node) => node.id === id) ?? null;
}

/** 根 → 节点 的路径；找不到返回空数组。 */
export function outlinePath(root: OutlineNode | null | undefined, id: string | null): OutlineNode[] {
  if (!root || !id) return [];
  const path: OutlineNode[] = [];
  function walk(node: OutlineNode): boolean {
    path.push(node);
    if (node.id === id) return true;
    for (const child of node.children) {
      if (walk(child)) return true;
    }
    path.pop();
    return false;
  }
  return walk(root) ? path : [];
}

/** 正文字数：不计空白，和原型 `charCount` 同口径。 */
export function charCount(text: string): number {
  return [...text.replace(/\s/g, "")].length;
}

/** 默认选中：第一个有正文的叶子（集级）；没有就选根。 */
export function defaultOutlineNode(root: OutlineNode | null | undefined): OutlineNode | null {
  if (!root) return null;
  const leaves = flattenOutline(root).filter((node) => node.children.length === 0);
  const withBody = leaves.find((node) => node.outline.trim().length > 0);
  return withBody ?? leaves[0] ?? root;
}

// ---------------------------------------------------------------- 剧本 / 分镜稿

export interface ScriptSceneGroup {
  scene: ScriptScene;
  rows: ScriptRow[];
}

/** 分镜稿行按 `sceneId` 分组；顺序跟 `script.scenes` 一致。 */
export function groupScriptRows(script: ScriptDoc | null | undefined): ScriptSceneGroup[] {
  if (!script) return [];
  return script.scenes.map((scene) => ({
    scene,
    rows: script.rows.filter((row) => row.sceneId === scene.id),
  }));
}

export function scriptRowsForScene(
  script: ScriptDoc | null | undefined,
  sceneId: string | null,
): ScriptRow[] {
  if (!script) return [];
  if (!sceneId) return script.rows;
  return script.rows.filter((row) => row.sceneId === sceneId);
}

export function findScriptRow(
  script: ScriptDoc | null | undefined,
  no: string | null,
): ScriptRow | null {
  if (!script || !no) return null;
  return script.rows.find((row) => row.no === no) ?? null;
}

export function scriptSceneOf(
  script: ScriptDoc | null | undefined,
  sceneId: string | null,
): ScriptScene | null {
  if (!script || !sceneId) return null;
  return script.scenes.find((scene) => scene.id === sceneId) ?? null;
}

/** 分镜稿行的“本场镜头”：同场次的所有行（镜号列表）。 */
export function siblingRows(
  script: ScriptDoc | null | undefined,
  row: ScriptRow | null,
): ScriptRow[] {
  if (!script || !row) return [];
  return script.rows.filter((item) => item.sceneId === row.sceneId);
}

/** 正文侧栏 / 分镜稿筛选共用的场次选项。 */
export function scriptSceneOptions(
  script: ScriptDoc | null | undefined,
): { id: string; name: string; time: string; count: number }[] {
  if (!script) return [];
  return script.scenes.map((scene) => ({
    id: scene.id,
    name: scene.name,
    time: scene.time,
    count: script.rows.filter((row) => row.sceneId === scene.id).length,
  }));
}

/** 剧本层统计：拍 / 场 / 行。 */
export function scriptCounts(script: ScriptDoc | null | undefined): {
  beats: number;
  scenes: number;
  rows: number;
} {
  return {
    beats: script?.beats.length ?? 0,
    scenes: script?.scenes.length ?? 0,
    rows: script?.rows.length ?? 0,
  };
}

/** 场次名兜底（行引用了不存在的场次时也不空白）。 */
export function sceneNameOf(script: ScriptDoc | null | undefined, sceneId: string): string {
  return scriptSceneOf(script, sceneId)?.name ?? "未分场";
}

/** 当前集号：优先 storyboard，再退回项目集列表的第一集。 */
export function currentEpisodeNo(
  storyboard: StoryboardView | null | undefined,
  episodes: readonly EpisodeRef[],
): number | null {
  return storyboard?.episode.no ?? episodes[0]?.no ?? null;
}
